<template>
  <div class="ops-board" data-test="ops-board">
    <!-- Check-in: sticky on the left -->
    <div class="ops-board__left">
      <OpsColumn
        col-id="checkin"
        title="Check-in"
        :count="countOf(board.counts.checkin)"
        :sub="arrivedReady"
        :warn-a="waiting.a"
        :warn-b="waiting.b"
        :cards="board.checkin"
        :hover="hoverCol === 'checkin'"
        :empty-text="checkinEmpty"
        hover-text="Drop to move back"
        :skeleton="skeleton"
        :move="handleMove"
        sticky
        class="ops-board__col"
        @add="onAdd"
        @drag-start="onDragStart"
        @drag-end="onDragEnd"
      >
        <template #card="{ card }"><OpsPatientCard v-bind="cardProps(card, 'md')" v-on="cardListeners" /></template>
      </OpsColumn>
    </div>

    <!-- Rooms wrap into a grid. No sideways scroll; more than nine rooms get compact cards. -->
    <div class="ops-board__rooms" data-test="ops-rooms">
      <OpsColumn
        v-for="room in board.rooms"
        :key="room.opNum"
        variant="tile"
        :col-id="room.opNum"
        :title="room.label"
        :sub="roomSubtitle(room)"
        :cards="room.cards"
        :hover="hoverCol === String(room.opNum)"
        :busy="isBusy(room)"
        empty-text="Open"
        :hover-text="`Drop to seat in ${room.label}`"
        :skeleton="skeleton"
        :move="handleMove"
        @add="onAdd"
        @drag-start="onDragStart"
        @drag-end="onDragEnd"
      >
        <template #card="{ card }"><OpsPatientCard v-bind="cardProps(card, tileSize)" v-on="cardListeners" /></template>
      </OpsColumn>
      <div v-if="!board.rooms.length && !skeleton" class="ops-board__norooms" data-test="ops-no-rooms">No rooms are set up yet.</div>
    </div>

    <!-- Check-out and Alerts, stacked on the right -->
    <div class="ops-board__right">
      <OpsColumn
        col-id="checkout"
        title="Check-out"
        :count="countOf(board.counts.checkedOut + board.counts.dismissed)"
        :sub="checkoutSub"
        :cards="board.checkout"
        :hover="hoverCol === 'checkout'"
        empty-text="No one at check-out"
        hover-text="Drop to check out"
        :skeleton="skeleton"
        :move="handleMove"
        class="ops-board__col"
        @add="onAdd"
        @drag-start="onDragStart"
        @drag-end="onDragEnd"
      >
        <template #card="{ card }"><OpsPatientCard v-bind="cardProps(card, 'md')" v-on="cardListeners" /></template>
      </OpsColumn>

      <section class="ops-alerts" aria-label="Alerts" data-test="ops-alerts">
        <div class="ops-alerts__head">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#0f7259" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></svg>
          <span class="ops-alerts__title">Alerts</span>
          <span class="ops-alerts__count" data-test="ops-alerts-count">{{ pings.pings.length ? pings.pings.length : '' }}</span>
          <button type="button" class="ops-alerts__add" aria-label="Send alert" title="Send alert" data-test="ops-alerts-send" @click="$emit('send-alert')">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M12 5v14" /></svg>
          </button>
        </div>

        <!-- One chip per recipient (2+ recipients, or one with 3+ pings): tap to scroll to and highlight their pings. -->
        <div v-if="pings.hasLegend" class="ops-alerts__legend" data-test="ops-alerts-legend">
          <button
            v-for="entry in pings.legend"
            :key="entry.to"
            type="button"
            class="ops-alerts__chip"
            title="Jump to their alerts"
            :data-test="`ops-alerts-chip-${entry.to}`"
            @click="jumpTo(entry.to)"
          ><span class="ops-alerts__dot" :style="{ background: chipColor(entry) }"></span>{{ entry.label }}<span class="ops-alerts__n">{{ entry.n }}</span></button>
        </div>

        <div ref="alertsBox" class="ops-alerts__list">
          <OpsPingCard
            v-for="ping in pings.pings"
            :key="ping.key"
            :ping="ping"
            :highlight="hiTo === ping.to"
            @clear="$emit('clear-ping', $event)"
          />
          <div v-if="!pings.pings.length" class="ops-alerts__empty" data-test="ops-alerts-empty">No alerts. A ping stays here until someone clears it.</div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
/**
 * The Practice Ops Board (plan §6, "Design review"): a sticky Check-in column, the rooms in a wrapping grid, and
 * Check-out + Alerts stacked on the right. PRESENTATIONAL + the drag rules — it holds no board state and writes nothing.
 * It turns a drop (or the ⋯ menu) into ONE decision and reports it:
 *
 *   move-card({ card, from, to, kind })   a real move: 'seat' | 'checkout' | 'checkin'. The page commits it (moveCard).
 *   refused({ card, to, message })        a drop on an occupied room — the toast has already been shown
 *
 * THE RULES (utils/practiceOps/moves.js, plan behaviour calls 1 and 2):
 *   • a reorder inside one list is not a move (`resolveDragMove` → null, inside planMove): nothing is emitted, nothing is written;
 *   • ONE PATIENT PER ROOM: a drop on a room that holds someone else is refused with
 *     "<Room> is in use by <Last>, <First>. One patient per room." (`roomDropRefusal`, inside planMove);
 *   • NO READY GATE: nothing here asks whether a Check-in patient is Ready. `canMove` is the page's hook (its
 *     `onMove` override), asked while a card is dragged over a target; it can only veto, and the page never does.
 *
 * DRAGGING. vuedraggable (Sortable) with the whole card as the handle and the whole tile / column as the target
 * (OpsColumn). Lists are CONTROLLED — the DOM is put back after every drag and the board is rebuilt from the document.
 * `drag-start` / `drag-end` are the page's cue to hold back store updates while a card is in the air (window.__isDragging).
 *
 * The cards are OpsPatientCard; everything a card needs from the page (stripe colour, live note, birthday, provider
 * name) comes through the four small functions below, so this file knows nothing about the roster or the store.
 */
import OpsColumn from './OpsColumn.vue';
import OpsPatientCard from './OpsPatientCard.vue';
import OpsPingCard from './OpsPingCard.vue';
import { planMove } from '@/utils/practiceOps/moves';
import dropTracking from './dropTracking';
import { arrivedReadyLine, checkoutLine, waitingLine, roomSub } from '@/utils/practiceOps/boardText';
import { isTooLight } from '@/utils/scheduleView/provColor';
import { WALL_THEMES } from '@/utils/practiceOps/viewPref';

/** More rooms than this and the cards in a room tile are compact. */
const COMPACT_ROOMS_ABOVE = 9;

const EMPTY_BOARD_TEXT = 'No patients yet. Arrivals show here the moment they are marked in Open Dental.';

export default {
  name: 'OpsBoard',

  components: { OpsColumn, OpsPatientCard, OpsPingCard },

  mixins: [dropTracking],

  props: {
    /** board.js buildBoard() */
    board: { type: Object, required: true },
    /** { pings, legend, hasLegend } — boardText.js buildPings() */
    pings: { type: Object, default: () => ({ pings: [], legend: [], hasLegend: false }) },
    zone: { type: String, default: '' },
    nowMs: { type: Number, default: 0 },
    alertPayload: { type: Object, default: null },
    viewSettings: { type: Object, default: null },
    showAlertIcons: { type: Boolean, default: true },
    skeleton: { type: Boolean, default: false },
    /** card → '#rrggbb' (the provider's colour), card → { proc, note }, card → boolean, card → provider's name */
    stripeFor: { type: Function, default: () => '' },
    textFor: { type: Function, default: card => ({ proc: card.procDescript || '', note: card.note || '' }) },
    birthdayFor: { type: Function, default: () => false },
    providerFor: { type: Function, default: () => '' },
  },

  data() {
    return {
      /** The recipient whose pings were just jumped to (highlighted for a moment). */
      hiTo: null,
    };
  },

  computed: {
    tileSize() {
      return this.board.rooms.length > COMPACT_ROOMS_ABOVE ? 'sm' : 'md';
    },

    arrivedReady() {
      return arrivedReadyLine(this.board.counts);
    },

    waiting() {
      return waitingLine(this.board.counts, this.board.thresholdMin);
    },

    checkoutSub() {
      return checkoutLine(this.board.counts);
    },

    boardIsEmpty() {
      const c = this.board.counts;
      return c.checkin === 0 && c.inRooms === 0 && c.checkedOut === 0 && c.dismissed === 0;
    },

    checkinEmpty() {
      return this.boardIsEmpty ? EMPTY_BOARD_TEXT : 'No one waiting';
    },

    /** Where "Move to…" can send a card: Check-in and every free room. A card's own place is dropped by the card. */
    moveTargets() {
      const rooms = this.board.rooms.filter(r => !r.occupied).map(r => ({ id: r.opNum, label: r.label }));
      return [{ id: 'checkin', label: 'Check-in' }].concat(rooms);
    },

    /** One listener set for every card, so the three places that draw cards agree. */
    cardListeners() {
      return {
        ping: card => this.$emit('ping-card', card),
        'alert-click': payload => this.$emit('alert-click', payload),
        move: ({ card, to }) => this.requestMove(card, to),
        checkout: card => this.requestMove(card, 'checkout'),
        dismiss: card => this.$emit('dismiss-card', card),
      };
    },
  },

  beforeDestroy() {
    if (this.hiTimer) clearTimeout(this.hiTimer);
    this.hiTimer = null;
  },

  methods: {
    countOf(n) {
      return n > 0 ? String(n) : '';
    },

    roomSubtitle(room) {
      const first = room.cards[0];
      return roomSub(room, first ? this.providerFor(first) : '', this.nowMs);
    },

    /** Everything one OpsPatientCard is given (a v-bind object). */
    cardProps(card, size) {
      const text = this.textFor(card) || {};
      return {
        card,
        size,
        stripe: this.stripeFor(card) || '',
        proc: text.proc || '',
        note: text.note || '',
        zone: this.zone,
        alertPayload: this.alertPayload,
        viewSettings: this.viewSettings,
        showAlertIcons: this.showAlertIcons,
        birthday: !!this.birthdayFor(card),
        moveTargets: this.moveTargets,
        canDismiss: card.column === 'checkout' && !card.dismissed,
      };
    },

    chipColor(entry) {
      const c = entry && entry.color;
      return typeof c === 'string' && /^#[0-9a-f]{6}$/i.test(c) && !isTooLight(c) ? c : WALL_THEMES.light.stripeFallback;
    },

    isBusy(room) {
      return this.hoverCol === String(room.opNum) && room.occupied && this.dragFrom !== String(room.opNum);
    },

    // ── drag ────────────────────────────────────────────────────────────────────────────────────
    // onDragStart / onDragEnd / handleMove come from dropTracking.js (shared with the Operatory view).

    /** A card from another list landed on `to`. */
    onAdd({ to, card }) {
      this.requestMove(card, to);
    },

    /** The one decision behind a drop AND the ⋯ menu (moves.js planMove). See the header. */
    requestMove(card, to) {
      const plan = planMove({ card, to, rooms: this.board.rooms });
      if (!plan) return;   // a reorder inside one list, or an unknown side: write nothing
      if (plan.refused) {
        if (this.$toast && typeof this.$toast.error === 'function') this.$toast.error(plan.message);
        this.$emit('refused', { card, to: plan.to, message: plan.message });
        return;
      }
      this.$emit('move-card', { card, from: plan.from, to: plan.to, kind: plan.kind });
    },

    // ── the Alerts column ───────────────────────────────────────────────────────────────────────
    /** A recipient chip: scroll that person's first ping into view and highlight theirs for a moment. */
    jumpTo(to) {
      const box = this.$refs.alertsBox;
      if (box) {
        const target = Array.from(box.children).find(el => el.getAttribute && el.getAttribute('data-to') === to);
        if (target) {
          const top = Math.max(0, target.offsetTop - box.offsetTop - 4);
          if (typeof box.scrollTo === 'function') box.scrollTo({ top, behavior: 'smooth' });
          else box.scrollTop = top;
        }
      }
      this.hiTo = to;
      if (this.hiTimer) clearTimeout(this.hiTimer);
      this.hiTimer = setTimeout(() => { this.hiTo = null; this.hiTimer = null; }, 1600);
    },
  },
};
</script>

<style lang="scss" scoped>
.ops-board {
  display: flex;
  gap: 10px;
  height: calc(100vh - 210px);
  min-height: 520px;
  position: relative;
}

.ops-board__left {
  flex: none;
  width: 232px;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.ops-board__col {
  flex: 1 1 0;
}

.ops-board__rooms {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(176px, 1fr));
  grid-auto-rows: minmax(150px, auto);
  gap: 8px;
  align-content: start;
  padding-right: 2px;
}

.ops-board__norooms {
  grid-column: 1 / -1;
  padding: 24px;
  text-align: center;
  color: #707876;
  font-size: 13px;
}

.ops-board__right {
  flex: none;
  width: 240px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
}

.ops-alerts {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: 1px solid #dfe4e3;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(12, 15, 14, 0.05);
}

.ops-alerts__head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 8px 9px 12px;
}

.ops-alerts__title {
  flex: 1;
  font-size: 14px;
  font-weight: 800;
}

.ops-alerts__count {
  font-size: 12px;
  font-weight: 700;
  color: #707876;
}

.ops-alerts__add {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #3a4240;
  cursor: pointer;

  &:hover { background: #eff3f2; }
}

.ops-alerts__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 0 10px 8px 12px;
}

.ops-alerts__chip {
  display: flex;
  align-items: center;
  gap: 5px;
  height: 24px;
  padding: 0 8px;
  border: 1px solid #dfe4e3;
  border-radius: 999px;
  background: #ffffff;
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 700;
  color: #3a4240;
  cursor: pointer;

  &:hover { background: #eff3f2; }
}

.ops-alerts__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.ops-alerts__n { color: #707876; }

.ops-alerts__list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #c8cfcd transparent;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 4px 8px 8px;
}

.ops-alerts__empty {
  border: 1.5px dashed #d5dcda;
  border-radius: 12px;
  padding: 16px 12px;
  font-size: 12.5px;
  line-height: 1.45;
  color: #707876;
  text-align: center;
}

// Narrow windows: stack the three areas, no sideways scroll.
@media (max-width: 899px) {
  .ops-board {
    flex-direction: column;
    height: auto;
    min-height: 0;
  }

  .ops-board__left,
  .ops-board__right {
    width: auto;
  }

  .ops-board__col { max-height: 420px; }
  .ops-board__rooms { overflow: visible; }
  .ops-alerts { max-height: 420px; }
}
</style>
